import { Grid, Typography } from "@mui/material";
import { useEffect, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog, { IDefaultDialogProps } from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import FormHelper from "../../../../helpers/FormHelper";
import SelectField from "../../../../components/SelectField";
import { useAuthContext } from "../../../../contexts/AuthContext";
import { IRoomBlocking } from "../../../../interfaces/IPartnerPms";
import DateTimeHelper from "../../../../helpers/DateTimeHelper";
import PartnerPmsService from "../../../../services/PartnerPmsService";
import DateField from "../../../../components/DateField";
import useState from "../../../../hooks/useState";
import useDialog from "../../../../hooks/useDialog";


interface IStateValues {
    roomId: number | undefined;
    startDate: Date | undefined;
    endDate: Date | undefined;
    typeId: number | undefined;
    description: string;
}

const AddDialog: React.FunctionComponent<IAddEditDialogProps<IRoomBlocking>> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {}
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();

    const state = useState<IStateValues>({
        roomId: undefined,
        startDate: undefined,
        endDate: undefined,
        typeId: undefined,
        description: ''
    });

    const confirmationDialog = useDialog();


    useEffect(() => {
        if (open) {
            confirmationDialog.handleClose();

            if (data) {
                state.update('roomId', data.roomId);
                state.update('startDate', data.startDate);
                state.update('endDate', data.endDate);
                state.update('typeId', data.typeId);
                state.update('description', data.description);
            } else {
                const now = DateTimeHelper.ToDate(undefined);

                state.reset();
                state.update('startDate', now);
                state.update('endDate', now);
            }
        }

        // eslint-disable-next-line
    }, [open]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const formData = FormHelper.Create(event);

        PartnerPmsService.AddRoomBlocking(formData).then(() => {
            handleClose();
            handleListRefresh();
        });
        // eslint-disable-next-line
    }, [data]);


    const dialogActions = useMemo(() => {
        const response = [<ActionButton action='cancel' onClick={handleClose} />];

        const { roomId, typeId, startDate, endDate, description } = state.values;

        if (roomId && typeId && startDate && endDate && description && description.length > 0) {
            response.push(<ActionButton action='save' onClick={confirmationDialog.handleOpen} />);
        } else {
            response.push(<ActionButton action='save' type='submit' />);
        }

        return response;
        // eslint-disable-next-line
    }, [translate, state.values]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data ? 'edit' : 'add')}
            maxWidth='sm'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={4} xs={12}>
                    <SelectField
                        label={translate('room')}
                        name='roomId'
                        value={state.values.roomId}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={(enterprise?.partnerPms.rooms ?? []).map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        })}
                    />
                </Grid>

                <Grid item md={8} xs={12}>
                    <SelectField
                        label={translate('blockingType')}
                        name='typeId'
                        value={state.values.typeId}
                        onChange={e => state.update(e.target.name, e.target.value)}
                        data={(enterprise?.partnerPms.roomBlockingTypes ?? []).map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        })}
                    />
                </Grid>
                
                <Grid item md={6} xs={12}>
                    <DateField
                        label={translate('startDate')}
                        format='DD/MM/YYYY'
                        name='startDate'
                        value={state.values.startDate}
                        onChange={value => state.update('startDate', value)}
                        disablePast={true}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <DateField
                        label={translate('endDate')}
                        format='DD/MM/YYYY'
                        name='endDate'
                        value={state.values.endDate}
                        onChange={value => state.update('endDate', value)}
                        disablePast={true}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField 
                        label={translate('description')} 
                        name='description' 
                        value={state.values.description} 
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 100))}
                        multiline={true}
                        minRows={6}
                        maxRows={6}
                    />
                </Grid>
            </Grid>

            <ConfirmationDialog
                open={confirmationDialog.open}
                handleClose={() => confirmationDialog.handleClose()}
            />
        </Dialog>
    );
}

export default AddDialog;




const ConfirmationDialog: React.FunctionComponent<IDefaultDialogProps> = ({
    open,
    handleClose
}) => {
    const { translate } = useLanguageContext();

    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='no' onClick={handleClose} />,
            <ActionButton action='yes' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate('attention')}
            dialogActions={dialogActions}
            className='dialogactions-spacebetween overflow-inherit'
            absolute={true}
            dividers={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item xs={12}>
                    <Typography variant='body1'>
                        {translate('_roomBlockingDialogAddConfirmationDescription')}
                    </Typography>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant='body1'>
                        {translate('_proceedActionAskDescription')}
                    </Typography>
                </Grid>
            </Grid>
        </Dialog>
    );
}